import { Component } from '@angular/core';
import * as moment from 'moment';
import { OpenConnectionVm } from 'src/app/models/backoffice';
import { ServerService } from 'src/app/services/server/server.service';

interface OpenConnectionTileVm extends OpenConnectionVm {
  memberNames: string[];
  memberPictures: string[];
  categories: string[];
}

interface MeterReasoningEntry {
  key: string;
  label: string;
  value: string;
}

interface OpenConnectionsPagedResult {
  items: OpenConnectionVm[];
  totalCount: number;
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-open-connections-page',
  templateUrl: './open-connections-page.component.html',
  styleUrls: ['./open-connections-page.component.scss'],
  standalone: false,
})
export class OpenConnectionsPageComponent {
  readonly defaultUserImage = 'assets/images/static/user.png';
  readonly pageSizeOptions = [500, 1000, 2000, 4000];
  readonly createdDatePresets: Array<{ key: string; label: string; days: number }> = [
    { key: '7d', label: '7 days ago', days: 7 },
    { key: '14d', label: '2 weeks ago', days: 14 },
    { key: '30d', label: '1 month ago', days: 30 },
  ];
  readonly statusOptions: Array<{ label: string; value: number | null }> = [
    { label: 'All statuses', value: 0 },
    { label: 'Joining', value: 1 },
    { label: 'Open', value: 2 },
    { label: 'Closed', value: 3 },
  ];

  loading = false;
  errorMessage = '';
  items: OpenConnectionTileVm[] = [];
  totalCount = 0;
  totalPages = 1;
  page = 1;
  pageSize = 500;

  selectedStatus: number | null = 2;
  createdDatePreset = '';
  activeDateFilterLabel = 'None';
  customDateFrom = '';
  customDateTo = '';
  showCustomDateModal = false;

  selectedImageUrl: string | null = null;
  selectedImageAlt = 'Profile picture';
  selectedMeterTitle = '';
  selectedMeterEntries: MeterReasoningEntry[] = [];
  selectedMeterRaw = '';

  constructor(private server: ServerService) {
    this.refresh();
  }

  refresh() {
    this.loading = true;
    this.errorMessage = '';
    const connectionStatus = this.getConnectionStatusFilter();
    const { fromTs, toTs } = this.getCreatedRangeIso();

    this.server
      .onGetOpenConnections(connectionStatus, fromTs, toTs, this.page, this.pageSize)
      .subscribe({
      next: (res) => {
        if (res.success === true) {
          const parsed = this.parsePagedResult(res.obj);
          this.totalCount = parsed.totalCount;
          this.page = parsed.page;
          this.pageSize = parsed.pageSize;
          this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
          this.items = parsed.items.map((item) =>
            this.toTileVm(item)
          );
        } else {
          this.items = [];
          this.totalCount = 0;
          this.totalPages = 1;
          this.errorMessage = res.message || 'Unable to load open connections.';
        }
        this.loading = false;
      },
      error: (err) => {
        console.log(err);
        this.items = [];
        this.totalCount = 0;
        this.totalPages = 1;
        this.errorMessage = 'Unable to load open connections right now.';
        this.loading = false;
      },
    });
  }

  applyFilters(resetPage: boolean = true) {
    if (resetPage) {
      this.page = 1;
    }
    this.refresh();
  }

  clearFilters() {
    this.selectedStatus = 2;
    this.createdDatePreset = '';
    this.activeDateFilterLabel = 'None';
    this.customDateFrom = '';
    this.customDateTo = '';
    this.showCustomDateModal = false;
    this.page = 1;
    this.pageSize = 500;
    this.refresh();
  }

  onPresetCreatedDateChange() {
    if (this.createdDatePreset === 'custom') {
      this.openCustomDateModal();
      return;
    }

    if (this.createdDatePreset) {
      const preset = this.createdDatePresets.find((x) => x.key === this.createdDatePreset);
      this.activeDateFilterLabel = preset ? `Created on/before ${preset.label}` : 'None';
    } else {
      this.activeDateFilterLabel = 'None';
    }

    this.applyFilters();
  }

  openCustomDateModal() {
    const today = moment().format('YYYY-MM-DD');
    this.customDateFrom = this.customDateFrom || today;
    this.customDateTo = this.customDateTo || today;
    this.showCustomDateModal = true;
  }

  closeCustomDateModal() {
    if (this.createdDatePreset === 'custom' && !this.activeDateFilterLabel.startsWith('From')) {
      this.createdDatePreset = '';
    }
    this.showCustomDateModal = false;
  }

  applyCustomDateRange() {
    const from = moment(this.customDateFrom);
    const to = moment(this.customDateTo);
    if (!from.isValid() || !to.isValid()) return;

    const safeFrom = from.isAfter(to) ? to : from;
    const safeTo = from.isAfter(to) ? from : to;
    this.customDateFrom = safeFrom.format('YYYY-MM-DD');
    this.customDateTo = safeTo.format('YYYY-MM-DD');
    this.activeDateFilterLabel = `From ${safeFrom.format('YYYY-MM-DD')} to ${safeTo.format('YYYY-MM-DD')}`;
    this.showCustomDateModal = false;
    this.applyFilters();
  }

  onStatusChange() {
    this.applyFilters();
  }

  onPageSizeChange() {
    if (this.pageSize > 4000) this.pageSize = 4000;
    if (this.pageSize < 1) this.pageSize = 500;
    this.page = 1;
    this.refresh();
  }

  goToPreviousPage() {
    if (this.page <= 1 || this.loading) return;
    this.page -= 1;
    this.refresh();
  }

  goToNextPage() {
    if (this.page >= this.totalPages || this.loading) return;
    this.page += 1;
    this.refresh();
  }

  getExpiryText(expiryTs: string): string {
    return this.getRelativeDateText(expiryTs, 'No expiry date');
  }

  getExpiryLabel(expiryTs: string): string {
    if (!expiryTs) {
      return 'Expiry unavailable';
    }
    const ts = moment(expiryTs);
    if (!ts.isValid()) {
      return 'Expiry unavailable';
    }
    return ts.isBefore(moment()) ? 'Expired' : 'Expires';
  }

  getCreatedText(createdTs: string): string {
    return this.getRelativeDateText(createdTs, 'Unknown');
  }

  getLastActivityText(meterReasoning: string): string {
    const rawLastAct = this.getReasoningValue(meterReasoning, 'lastAct');
    return this.getRelativeDateText(rawLastAct, '-');
  }

  getActivityStyle(score: number) {
    const safeScore = this.getSafeScore(score);
    return {
      background: `conic-gradient(#10b981 ${safeScore * 3.6}deg, #e5e7eb 0deg)`,
    };
  }

  getSafeScore(score: number): number {
    const n = Number(score);
    if (Number.isNaN(n)) {
      return 0;
    }
    return Math.max(0, Math.min(100, n));
  }

  getMeterClass(meter: string): string {
    const key = (meter || '').trim().toLowerCase();
    if (key === 'quiet') return 'meter-quiet';
    if (key === 'warm' || key === 'warming up') return 'meter-warm';
    if (key === 'flowing') return 'meter-flowing';
    if (key === 'electric') return 'meter-electric';
    return 'meter-default';
  }

  getConnectionStatusLabel(status: number): string {
    if (status === 1) return 'Joining';
    if (status === 2) return 'Open';
    if (status === 3) return 'Closed';
    return `Status ${status}`;
  }

  getConnectionStatusClass(status: number): string {
    if (status === 1) return 'status-joining';
    if (status === 2) return 'status-open';
    if (status === 3) return 'status-closed';
    return 'status-default';
  }

  getMemberPicture(item: OpenConnectionTileVm, idx: number): string {
    const pic = item.memberPictures[idx] || null;
    return this.server.ProcessProfilePicUrl(pic, false, true);
  }

  onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = this.defaultUserImage;
  }

  openImageModal(item: OpenConnectionTileVm, idx: number, name: string) {
    this.selectedImageUrl = this.getMemberPicture(item, idx);
    this.selectedImageAlt = name || 'Profile picture';
  }

  closeImageModal() {
    this.selectedImageUrl = null;
  }

  openMeterReasoningModal(item: OpenConnectionTileVm) {
    this.selectedMeterTitle = item.connectionName || `Connection #${item.connectionId}`;
    this.selectedMeterEntries = this.parseMeterReasoning(item.meterReasoning);
    this.selectedMeterRaw = item.meterReasoning || '';
  }

  closeMeterReasoningModal() {
    this.selectedMeterTitle = '';
    this.selectedMeterEntries = [];
    this.selectedMeterRaw = '';
  }

  private toTileVm(item: OpenConnectionVm): OpenConnectionTileVm {
    return {
      ...item,
      memberNames: this.splitCsv(item.members),
      memberPictures: this.splitCsv(item.memberProfilePictures),
      categories: this.splitCsv(item.categoriesUsed),
    };
  }

  private splitCsv(raw: string): string[] {
    if (!raw) return [];
    return raw
      .split(',')
      .map((x) => x.trim())
      .filter((x) => x.length > 0);
  }

  private getRelativeDateText(value: string, emptyFallback: string): string {
    if (!value) {
      return emptyFallback;
    }
    const ts = moment(value);
    if (!ts.isValid()) {
      return emptyFallback;
    }
    return ts.fromNow();
  }

  private getConnectionStatusFilter(): number | null {
    return this.selectedStatus;
  }

  private getCreatedRangeIso(): { fromTs: string | null; toTs: string | null } {
    if (this.createdDatePreset && this.createdDatePreset !== 'custom') {
      const preset = this.createdDatePresets.find((x) => x.key === this.createdDatePreset);
      if (!preset) return { fromTs: null, toTs: null };
      const to = moment().subtract(preset.days, 'days').endOf('day').toISOString();
      return { fromTs: null, toTs: to };
    }

    if (this.createdDatePreset === 'custom' && this.customDateFrom && this.customDateTo) {
      const from = moment(this.customDateFrom).startOf('day');
      const to = moment(this.customDateTo).endOf('day');
      if (!from.isValid() || !to.isValid()) return { fromTs: null, toTs: null };
      return { fromTs: from.toISOString(), toTs: to.toISOString() };
    }

    return { fromTs: null, toTs: null };
  }

  private parsePagedResult(obj: any): OpenConnectionsPagedResult {
    if (Array.isArray(obj)) {
      return {
        items: obj as OpenConnectionVm[],
        totalCount: obj.length,
        page: this.page,
        pageSize: this.pageSize,
      };
    }

    return {
      items: Array.isArray(obj?.items) ? (obj.items as OpenConnectionVm[]) : [],
      totalCount: Number(obj?.totalCount) || 0,
      page: Number(obj?.page) || this.page,
      pageSize: Number(obj?.pageSize) || this.pageSize,
    };
  }

  private getReasoningValue(raw: string, key: string): string {
    if (!raw || !key) return '';
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = raw.match(new RegExp(`(?:^|\\s)${escapedKey}=([^\\s]+)`));
    return match?.[1] || '';
  }

  private parseMeterReasoning(raw: string): MeterReasoningEntry[] {
    if (!raw || raw.trim().length === 0) return [];

    return raw
      .trim()
      .split(/\s+/)
      .map((part) => {
        const eqIdx = part.indexOf('=');
        if (eqIdx < 0) return null;
        const key = part.slice(0, eqIdx).trim();
        const value = part.slice(eqIdx + 1).trim();
        if (!key) return null;
        return {
          key,
          label: this.humanizeReasoningKey(key),
          value: this.humanizeReasoningValue(key, value),
        } as MeterReasoningEntry;
      })
      .filter((x): x is MeterReasoningEntry => x !== null);
  }

  private humanizeReasoningKey(key: string): string {
    const map: Record<string, string> = {
      fresh: 'Freshness Score',
      resp: 'Responses',
      bal: 'Balance Score',
      mom: 'Momentum Score',
      medianReplySec: 'Median Reply Time',
      participants: 'Participants',
      totalEvents: 'Total Events',
      totalAlt: 'Alternative Events',
      answeredQs: 'Answered Questions',
      answeredQUsers: 'Users Who Answered',
      qDiff: 'Question Difference',
      qAlt: 'Alternative Questions',
      mutualInit: 'Mutual Initiation',
      highTempo: 'High Tempo',
      flowingEligible: 'Flowing Eligible',
      lastAct: 'Last Activity',
    };

    return map[key] || key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  }

  private humanizeReasoningValue(key: string, value: string): string {
    if (!value || value.toLowerCase() === 'null') {
      return 'N/A';
    }

    if (value === 'True' || value === 'False') {
      return value === 'True' ? 'Yes' : 'No';
    }

    if (key === 'medianReplySec') {
      const sec = Number(value);
      if (!Number.isNaN(sec)) {
        if (sec < 60) return `${sec}s`;
        const min = Math.floor(sec / 60);
        const rem = sec % 60;
        return rem > 0 ? `${min}m ${rem}s` : `${min}m`;
      }
    }

    if (key === 'lastAct') {
      const ts = moment(value);
      if (ts.isValid()) {
        return `${ts.fromNow()} (${ts.format('YYYY-MM-DD HH:mm')})`;
      }
    }

    return value;
  }
}
